import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  TextField,
  Button,
  Modal,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import "../../components/Table/Tables.css";
import Pagination from "../../components/Pagination";
import CloseIcon from "@mui/icons-material/Close";
import { API_BASE } from '../../utils/api';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TableCat = ({ searchTerm }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editedCategory, setEditedCategory] = useState({});
  const [newImage, setNewImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 40;

  const [touchStartY, setTouchStartY] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);

  const handleTouchStart = (e) => {
    setTouchStartY(e.touches[0].clientY);
    setIsScrolling(false);
  };

  const handleTouchMove = (e) => {
    const deltaY = Math.abs(e.touches[0].clientY - touchStartY);
    if (deltaY > 10) {
      setIsScrolling(true);
    }
  };

  const handleTouchEnd = (e, category) => {
    if (!isScrolling) {
      handleViewClick(category, e); // pass event to prevent propagation
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/categories/all`);
        if (!response.ok) throw new Error("Failed to fetch categories");
        const data = await response.json();
        setCategories(data);
        setTotalPages(Math.ceil(data.length / itemsPerPage));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentCategories = filteredCategories.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewClick = (category, e) => {
    if (e) e.stopPropagation(); // prevent accidental click on modal buttons
    setSelectedCategory(category);
    setEditedCategory({ ...category });
    setNewImage(null);

    // Small delay to avoid immediate click on modal buttons
    setTimeout(() => {
      setOpenModal(true);
      window.scrollTo({ top: 0, behavior: "smooth" }); // ensure modal is visible
    }, 100);
  };

  const handleEditChange = (field, value) => {
    setEditedCategory((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (event) => {
    setNewImage(event.target.files[0]);
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append("name", editedCategory.name);
    formData.append("description", editedCategory.description);
    formData.append("isVisible", editedCategory.isVisible);
    if (newImage) {
      formData.append("image", newImage);
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/categories/${editedCategory.id}`,
        {
          method: "PUT",
          body: formData,
        }
      );
      if (!response.ok) throw new Error("Failed to update category");
      const updatedCategory = await response.json();
      setCategories((prev) =>
        prev.map((category) =>
          category.id === editedCategory.id ? updatedCategory : category
        )
      );
      setEditedCategory(updatedCategory);
      toast.success("Category updated successfully!");
      setOpenModal(false);
    } catch (err) {
      console.error("Error updating category:", err);
      toast.error("Failed to update category.");
    }
  };

  return (
    <Box className="main-container">
      <Box className="content-container">
        <Grid container spacing={2}>
          {searchTerm && filteredCategories.length === 0 ? (
            <Typography
              variant="h7"
              sx={{
                textAlign: "center",
                color: "black",
                width: "100%",
                fontFamily: "Roboto",
                marginTop: 2,
              }}
            >
              No results found
            </Typography>
          ) : (
            filteredCategories.map((category) => (
              <Grid
                item
                key={category.id}
                xs={6}
                sm={4}
                md={4}
                lg={2}
                onDoubleClick={(e) => handleViewClick(category, e)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={(e) => handleTouchEnd(e, category)}
              >
                <Card className="category-card">
                  {category.images && category.images.length > 0 ? (
                    <CardMedia
                      component="img"
                      height="140"
                      image={`${API_BASE}${category.images[0]}`}
                      alt={category.name}
                      className="category-image"
                    />
                  ) : (
                    <Typography>No Image</Typography>
                  )}
                  <CardContent>
                    <Typography variant="h6">{category.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {category.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        <div className="pagination-container">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </Box>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box className="modal-box">
          <CloseIcon
            onClick={() => setOpenModal(false)}
            className="close-icon"
          />

          <Typography variant="h6" gutterBottom>
            Edit Category
          </Typography>

          <TextField
            label="Category Name"
            variant="outlined"
            fullWidth
            margin="normal"
            value={editedCategory.name || ""}
            onChange={(e) => handleEditChange("name", e.target.value)}
          />

          <TextField
            label="Category Description"
            variant="outlined"
            fullWidth
            margin="normal"
            value={editedCategory.description || ""}
            onChange={(e) => handleEditChange("description", e.target.value)}
          />

          {editedCategory.images && editedCategory.images.length > 0 && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="body2">Current Image:</Typography>
              <img
                src={`${API_BASE}${editedCategory.images[0]}`}
                alt={editedCategory.name}
                className="table-image"
              />
            </Box>
          )}

          {newImage && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="body2">Selected Image</Typography>
              <img
                src={URL.createObjectURL(newImage)}
                alt="New"
                className="table-image"
              />
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <input type="file" onChange={handleImageChange} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={!!editedCategory.isVisible}
                  onChange={(e) =>
                    handleEditChange("isVisible", e.target.checked)
                  }
                  color="primary"
                />
              }
              label="Is Available"
            />
          </Box>

          <Box className="button-container">
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      </Modal>

      <ToastContainer position="top-center" autoClose={3000} />
    </Box>
  );
};

export default TableCat;
